
import { HttpClient } from '@angular/common/http';
import { EventEmitter, Inject, Injectable, OnDestroy } from '@angular/core';

import { SVG, Svg, Text as SvgText } from '@svgdotjs/svg.js';
import { ProductDesignerService } from 'app/product-designer.service';
import { StageConstants } from 'core/constants';
import { CriticalError, TrivialError } from 'core/error/error';
import { ErrorService } from 'core/error/error.service';
import { Utils } from 'core/utils';
import { AsyncEventEmitter, AsyncEventEmitterSubscriber
       } from 'core/utils/async-event-emitter';
import { StageUtils } from 'core/utils.stage';
import { TemplateConfig } from '../collage-designer-config';
import { DesignStepService } from '../design-step/design-step.service';
import { StageConfig } from './stage-config';
import { SVGValidationService } from './svg-validation.service';

interface StageState
{
  id: string;
  stageConfig: StageConfig;
  stageReference: Svg | undefined;
}

interface WorkingTemplateState {
  productId: string;
  stepId: number;
  templateId: string;
}

export enum StageFeedback {
  HIGHLIGHT_UNCHANGED_PLACEHOLDERS
}

class InlineSVGLoadingError extends TrivialError
{
  // i18n.error.inlineSVGLoadingError.title
  // i18n.error.inlineSVGLoadingError.message
  protected _name: string = 'InlineSVGLoadingError';
}

class TemplateLoadingError extends TrivialError
{
  // i18n.error.templateLoadingError.title
  // i18n.error.templateLoadingError.message
  protected _name: string = 'TemplateLoadingError';
}

class CriticalTemplateLoadingError extends CriticalError
{
  protected _name: string = 'CriticalTemplateLoadingError';
}

@Injectable()
export class StageService implements OnDestroy
{
  public stageConfigChanged: AsyncEventEmitter<StageConfig>
      = new AsyncEventEmitter();
  public stageConfigApplied: AsyncEventEmitter<boolean>
      = new AsyncEventEmitter();
  public feedbackRequested: EventEmitter<StageFeedback> = new EventEmitter();

  public isComponentTemplateSwitchRunning: boolean = false;

  private currentStageState: StageState;
  private templates: {[id: string]: string} = {};
  private http: HttpClient;
  private designStepService: DesignStepService;
  private svgValidationService: SVGValidationService;
  private errorService: ErrorService;
  private templateChangedSubscription:
      AsyncEventEmitterSubscriber<TemplateConfig>;
  private isStageLoaded: boolean = false;

  private lastWorkingTemplate: WorkingTemplateState | undefined = undefined;
  private inlineSVGsOverlayCache: {[location: string]: string} = {};
  private currentSwitchConfig: TemplateConfig|undefined = undefined;

  constructor(
      @Inject(HttpClient) http: HttpClient,
      @Inject(DesignStepService) designStepService: DesignStepService,
      @Inject(SVGValidationService) svgValidationService: SVGValidationService,
      @Inject(ErrorService) errorService: ErrorService,
      @Inject(ProductDesignerService) private productDesignerService:
          ProductDesignerService
  )
  {
    this.http = http;
    this.designStepService = designStepService;
    this.svgValidationService = svgValidationService;
    this.errorService = errorService;

    this.templateChangedSubscription =
        this.designStepService.templateChanged.subscribe(
            async (templateConfig) => {
              await this.switchToTemplate(templateConfig);
            });
  }

  public ngOnDestroy(): void
  {
    this.designStepService.templateChanged.unsubscribe(
        this.templateChangedSubscription);
  }

  public requestFeedback(feedback: StageFeedback): void
  {
    this.feedbackRequested.emit(feedback);
  }

  private async resolveTemplate(svgStructureURL: string): Promise<string>
  {
    const resp = <any> await this.http.get(svgStructureURL,
        { observe: 'response', responseType: 'text' })
      .pipe(Utils.retryPipe())
      .toPromise()
      .catch((exc) => {
        throw exc;
      });

    return StageUtils.ensureHasPapNamespace(resp.body);
  }

  public async getInlineSVGOverlay(location: string): Promise<string>
  {
    if (!(location in this.inlineSVGsOverlayCache)) {
      this.inlineSVGsOverlayCache[location] = <any> await this.http.get(
          location, { responseType: 'text' })
        .pipe(Utils.retryPipe())
        .toPromise()
        .catch((exc) => {
          const message = `[${exc.status} ${exc.statusText}] "${exc.url}"`
            .trim();
          const reference = [location];
          this.errorService.add(new InlineSVGLoadingError(message, reference),
              'StageService');
          this.errorService.flush('StageService');
        });
    }

    return this.inlineSVGsOverlayCache[location];
  }

  private async updateLastWorkingTemplate(): Promise<void>
  {
    this.lastWorkingTemplate = {
      productId: this.designStepService.getProductId(),
      stepId: this.designStepService.getDesignStepId(),
      templateId: (await this.designStepService.getTemplate()).id
    };
  }

  private async revertToLastWorkingTemplate(err: any): Promise<void>
  {
    const productId = this.designStepService.getProductId();
    const stepId = this.designStepService.getDesignStepId();
    const template = await this.designStepService.getTemplate();

    await this.designStepService.removeTemplate(template.id);

    const lastWorking = (
      this.lastWorkingTemplate &&
        this.lastWorkingTemplate.productId === productId &&
        this.lastWorkingTemplate.stepId === stepId)
      ? this.lastWorkingTemplate.templateId
      : undefined;

    const availableTemplates = await this.designStepService.getTemplates();
    const nextTemplate = availableTemplates[
      Object.keys(availableTemplates)[0]];

    const recoverWith = lastWorking || (nextTemplate && nextTemplate.id);

    if (recoverWith) {
      await this.designStepService.setTemplateId(recoverWith);
    }

    const message = `[${err.status} ${err.statusText}] "${err.url}"`.trim();
    const reference = [ template.title, err.url ];

    const error = !!recoverWith
      ? new TemplateLoadingError(message, reference)
      : new CriticalTemplateLoadingError(message, reference,
          err.status !== 0);

    this.errorService.add(error, 'StageService');
    this.errorService.flush('StageService');
  }

  public async refreshStageConfig(): Promise<void>
  {
    return this.switchToTemplate(await this.designStepService.getTemplate());
  }

  /**
   * @throws SVGValidationError
   */
  private async switchToTemplate(templateConfig: TemplateConfig): Promise<void>
  {
    if (this.currentSwitchConfig || this.isComponentTemplateSwitchRunning) {
      // This if case is (currently) important to prevent duplicate stage
      // updates. However, it also leads to problems if we want to change
      // design steps automatically and quickly.

      // We can only ignore switches if the template is the same as the current.
      if (this.currentSwitchConfig === templateConfig) {
        return;
      }
      await Utils.sleep(100);
      return await this.switchToTemplate(templateConfig);
    }
    try {
      this.currentSwitchConfig = templateConfig;
      this.productDesignerService.suspendDesignerTrigger.emit({suspend: true});
      const commit = await this.updateLastWorkingTemplate.bind(this);
      const rollback = await this.revertToLastWorkingTemplate.bind(this);

      const stageConfig = templateConfig.stageConfig;

      if (! stageConfig) {
        return;
      }

      stageConfig.designState = this.designStepService.getCurrentDesignState();

      const templateStateId = this.getTemplateStateId(stageConfig);
      if (templateStateId in this.templates) {
        await this.setStageConfigSVGStructure(
          stageConfig, this.templates[templateStateId]);
        commit();
      }
      else {
        try {
          const structure = await this.resolveTemplate(
            stageConfig.svgStructureURL);

          // zusätzliche Abfrage, sollte das Template angefordert werden,
          // während es gerade "heruntergeladen" wird
          if (!(templateStateId in this.templates)) {
            await this.setStageConfigSVGStructure(stageConfig, structure);
            this.templates[templateStateId] = structure;
            commit();
          }
        }
        catch (err) {
          rollback(err);
        }
      }
    }
    finally {
      this.productDesignerService.suspendDesignerTrigger.emit({suspend: false});
      this.currentSwitchConfig = undefined;
    }
  }

  /**
   * @throws SVGValidationError
   */
  private async setStageConfigSVGStructure(
    stageConfig: StageConfig, structure: string): Promise<void>
  {
    const templateStateId = this.getTemplateStateId(stageConfig);
    this.svgValidationService.validate(structure);

    stageConfig.svgStructure = structure;
    this.setCurrentStageState(templateStateId, stageConfig);
    await this.stageConfigChanged.emit(stageConfig);
  }

  public setCurrentStageState(id: string, stageConfig: StageConfig): void
  {
    this.currentStageState = <StageState> {
        id: id,
        stageConfig: stageConfig,
    };
  }

  public getCurrentStageState(): StageState
  {
    return this.currentStageState;
  }

  /**
   * @throws SVGValidationError
   */
  public async updateStageConfig(adaptedStageConfig: StageConfig): Promise<void>
  {
    if (! adaptedStageConfig) {
      return;
    }

    this.svgValidationService.validate(adaptedStageConfig.svgStructure);
    this.currentStageState.stageConfig = adaptedStageConfig;

    const templateStateId = this.getTemplateStateId(adaptedStageConfig);
    if (! templateStateId) {
      return;
    }

    this.templates[templateStateId] = adaptedStageConfig.svgStructure;

    try {
      await this.designStepService.setSVGStructure(
        adaptedStageConfig.designState,
        adaptedStageConfig.svgStructure);
    } catch (_) {
      // fail gracefully
    }
  }

  public setCurrentStageReference(stage: Svg): void
  {
    this.currentStageState.stageReference = stage;
  }

  public getCurrentStageReference(): Svg | undefined
  {
    if (!this.currentStageState) {
      return undefined;
    }

    return this.currentStageState.stageReference;
  }

  private getTemplateStateId(stageConfig: StageConfig): string | undefined
  {
    return stageConfig.svgStructureURL;
  }

  public clearElement(id: string): void
  {
    const stage = this.getCurrentStageReference();

    if (! stage) {
      return;
    }

    const element = stage.find(`#${id}`).at(0);

    if (! element) {
      return;
    }

    element.attr(StageConstants.ATTRIBUTE_IS_UNCHANGED, 'false');
    if (element.type === 'text') {
      const clearText = (text: SvgText) => {
        for (const line of (<any>text.children())) {
          line.node.textContent = '';
        }

        const modelStr = text.attr(StageConstants.ATTRIBUTE_TEXT_MODEL);
        if (modelStr) {
          const model = JSON.parse(modelStr);
          model.text = [];
          text.attr(StageConstants.ATTRIBUTE_TEXT_MODEL, JSON.stringify(model));
        }
      };

      clearText(element as SvgText);

      const copiedElements = StageUtils
        .svgSelectAsArray(stage, 'text')
        .filter(t => t.attr('pap:copy-from') === id);

      for (const copied of copiedElements) {
        clearText(copied as SvgText);
      }
    } else {
      // implement as required.
    }
  }

  public getIsStageLoaded(): boolean
  {
    return this.isStageLoaded;
  }

  public setIsStageLoaded(isStageLoaded: boolean): void
  {
    this.isStageLoaded = isStageLoaded;
  }
}
